import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Badge, Box, HStack, Link, VStack, Text, IconButton, Skeleton, SkeletonCircle, SimpleGrid } from "@chakra-ui/react";
import { HiOutlineCpuChip, HiStar, HiPhone, HiEnvelope, HiFlag, HiClock, HiArrowPath } from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { LeadService } from "@/services/LeadService";
import { toaster } from "@/components/ui/toaster";
import dayjs from "dayjs";
import LeadStatusBadge from "./components/LeadStatusBadge";
import LeadDataTab from "./components/lead/LeadDataTab";
import LeadTimelineTab from "./components/lead/LeadTimelineTab";
import LeadNotesTab from "./components/lead/LeadNotesTab";
import LeadInteractionsTab from "./components/lead/LeadInteractionsTab";
import LeadAttachmentsTab from "./components/lead/LeadAttachmentsTab";
import useLeadSingleActions from "./hooks/useLeadSingleActions";

const LeadDetailPage = () => {
    const { leadId } = useParams();
    const location = useLocation();

    const [lead, setLead] = useState(null);
    const [notes, setNotes] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isNotesLoading, setIsNotesLoading] = useState(true);
    const [notesSearch, setNotesSearch] = useState('');
    const [notesSortDirection, setNotesSortDirection] = useState('desc');
    const [debouncedNotesSearch, setDebouncedNotesSearch] = useState('');
    const [notesFetchKey, setNotesFetchKey] = useState(0);
    const [timelineFetchKey, setTimelineFetchKey] = useState(0);
    const [isTimelineLoading, setIsTimelineLoading] = useState(true);
    const [attachments, setAttachments] = useState([]);
    const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(true);
    const [isFavLoading, setIsFavLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(location.state?.tab ?? "data");
    const [highlightId, setHighlightId] = useState(location.state?.highlightId ?? null);

    const { openStatusModal, openNoteModal, openTimelineModal, modals: singleActionModals } = useLeadSingleActions({
        onStatusChanged: (_, updated) => setLead(prev => ({ ...prev, status: updated.status })),
        onNoteAdded: () => setNotesFetchKey(k => k + 1),
        onTimelineEntryAdded: () => setTimelineFetchKey(k => k + 1),
    });

    useSetPageHeader("Szczegóły leada", "", <HiOutlineCpuChip />);

    useEffect(() => {
        setIsLoading(true);
        LeadService.getById(leadId).then(setLead).finally(() => setIsLoading(false));
    }, [leadId]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedNotesSearch(notesSearch), 400);
        return () => clearTimeout(timer);
    }, [notesSearch]);

    useEffect(() => {
        setIsNotesLoading(true);
        LeadService.getNotes(leadId, 'createdAt', notesSortDirection, debouncedNotesSearch || null)
            .then(d => setNotes(d ?? []))
            .finally(() => setIsNotesLoading(false));
    }, [leadId, notesSortDirection, debouncedNotesSearch, notesFetchKey]);

    useEffect(() => {
        setIsTimelineLoading(true);
        LeadService.getTimeline(leadId).then(d => setTimeline(d ?? [])).finally(() => setIsTimelineLoading(false));
    }, [leadId, timelineFetchKey]);

    useEffect(() => {
        setIsAttachmentsLoading(true);
        LeadService.getAttachments(leadId).then(d => setAttachments(d ?? [])).finally(() => setIsAttachmentsLoading(false));
    }, [leadId]);

    const handleToggleFavorite = async () => {
        setIsFavLoading(true);
        const updated = await LeadService.toggleFavorite(leadId);
        setLead(prev => ({ ...prev, isFavourite: updated.isFavourite }));
        setIsFavLoading(false);
    };

    const handleHeaderAddNote = () => openNoteModal(leadId);

    const handleOpenAttachment = (att) => {
        if (!att.path) return;
        const url = `${process.env.REACT_APP_BASE_URL || '/mlapi'}/Lead/${leadId}/attachments/file?name=${encodeURIComponent(att.path)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleDownloadAttachment = async (att) => {
        if (!att.path) return;
        try {
            const response = await LeadService.downloadAttachment(leadId, att.path);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = att.name ?? att.path.split('/').pop();
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toaster.create({ type: "error", title: "Nie udało się pobrać pliku." });
        }
    };

    const handleUploadAttachments = async (files) => {
        const newAtts = await LeadService.addAttachments(leadId, files);
        setAttachments(prev => [...prev, ...(newAtts ?? [])]);
    };

    const handleDownloadAllZip = async () => {
        try {
            const response = await LeadService.downloadAllAttachmentsZip(leadId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `attachments_${leadId}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toaster.create({ type: "error", title: "Nie udało się pobrać załączników." });
        }
    };

    if (isLoading) {
        return (
            <VStack w="full" py={6} gap={4} align="flex-start">
                <Skeleton height="14px" width="160px" borderRadius={4} />
                <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                    <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                        <VStack align="flex-start" gap={3} flex={1} minW="200px">
                            <Skeleton height="26px" width="200px" borderRadius={6} />
                            <Skeleton height="22px" width="90px" borderRadius="999px" />
                            <Skeleton height="14px" width="140px" borderRadius={4} />
                            <Skeleton height="14px" width="180px" borderRadius={4} />
                        </VStack>
                        <HStack gap={2}>
                            <Skeleton height="32px" width="100px" borderRadius={8} />
                            <Skeleton height="32px" width="100px" borderRadius={8} />
                        </HStack>
                    </HStack>
                </Box>
                <HStack gap={4} borderBottom="1px solid" borderColor="gray.200" w="full" pb={3}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} height="14px" width="70px" borderRadius={4} />
                    ))}
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <Box key={i} bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                            <HStack gap={3} mb={4}>
                                <SkeletonCircle size="32px" />
                                <Skeleton height="16px" width="140px" borderRadius={4} />
                            </HStack>
                            <VStack align="stretch" gap={3}>
                                <Skeleton height="12px" width="100%" borderRadius={4} />
                                <Skeleton height="12px" width="80%" borderRadius={4} />
                            </VStack>
                        </Box>
                    ))}
                </SimpleGrid>
            </VStack>
        );
    }

    if (!lead) {
        return <VStack w="full" py={12} align="center"><Text color="gray.500">Nie znaleziono leada.</Text></VStack>;
    }

    const fullName = [lead.firstname, lead.lastname].filter(Boolean).join(" ") || "-";

    const TABS = [
        { key: "data", label: "Dane" },
        { key: "timeline", label: "Oś czasu", count: timeline.length },
        { key: "notes", label: "Notatki", count: notes.length },
        { key: "interactions", label: "Rozmowy" },
        { key: "attachments", label: "Załączniki", count: attachments.length },
    ];

    return (
        <>
            <VStack w="full" py={6} gap={4} align="flex-start">
                <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                    <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                        <VStack align="flex-start" gap={2} flex={1} minW="200px">
                            <HStack gap={2}>
                                <Text fontWeight={700} fontSize="2xl" color="gray.900">{fullName}</Text>
                                <IconButton variant="ghost" size="sm" aria-label="Ulubione" color={lead.isFavourite ? "yellow.400" : "gray.500"} loading={isFavLoading} onClick={handleToggleFavorite}>
                                    <HiStar />
                                </IconButton>
                            </HStack>
                            <Box as="button" cursor="pointer" onClick={() => openStatusModal(leadId, lead.status)} outline="none">
                                <LeadStatusBadge status={lead.status} />
                            </Box>
                            {(lead.company || lead.jobTitle) && (
                                <Text color="gray.500" fontSize="sm">{[lead.jobTitle, lead.company].filter(Boolean).join(" @ ")}</Text>
                            )}
                            {lead.phoneNumber && (
                                <HStack gap={2}>
                                    <Box color="gray.500" fontSize="sm" flexShrink={0}><HiPhone /></Box>
                                    <Link href={`tel:${lead.phoneNumber}`} color="gray.900" fontSize="sm">{lead.phoneNumber}</Link>
                                </HStack>
                            )}
                            {lead.email && (
                                <HStack gap={2}>
                                    <Box color="gray.500" fontSize="sm" flexShrink={0}><HiEnvelope /></Box>
                                    <Link href={`mailto:${lead.email}`} color="gray.900" fontSize="sm">{lead.email}</Link>
                                </HStack>
                            )}
                            <HStack gap={2}>
                                <Box color="gray.500" fontSize="sm" flexShrink={0}><HiFlag /></Box>
                                <Text color="gray.900" fontSize="sm">{lead.language ?? "Nieznany"}</Text>
                            </HStack>
                        </VStack>
                        <VStack align="flex-end" gap={2} flexShrink={0}>
                            <HStack gap={2}>
                                <Box color="gray.500" fontSize="sm" flexShrink={0}><HiClock /></Box>
                                <Text color="gray.500" fontSize="sm">Utworzono: <Text as="span" color="gray.900">{dayjs(lead.createdAt).format("DD.MM.YYYY HH:mm")}</Text></Text>
                            </HStack>
                            {lead.updatedAt && (
                                <HStack gap={2}>
                                    <Box color="gray.500" fontSize="sm" flexShrink={0}><HiArrowPath /></Box>
                                    <Text color="gray.500" fontSize="sm">Ostatnia aktywność: <Text as="span" color="gray.900">{dayjs(lead.updatedAt).format("DD.MM.YYYY HH:mm")}</Text></Text>
                                </HStack>
                            )}
                        </VStack>
                    </HStack>
                </Box>

                <Box w="full">
                    <HStack gap={0} borderBottom="1px solid" borderColor="gray.200" w="full">
                        {TABS.map(tab => (
                            <Box
                                key={tab.key} as="button" display="flex" alignItems="center" gap="6px" px={4} py={3} cursor="pointer"
                                color={activeTab === tab.key ? "gray.900" : "gray.500"}
                                borderBottomWidth="2px" borderBottomStyle="solid" borderBottomColor={activeTab === tab.key ? "purple.500" : "transparent"}
                                fontWeight={activeTab === tab.key ? 600 : 400} fontSize="sm" onClick={() => setActiveTab(tab.key)}
                                transition="all 0.15s" mb="-1px" bg="transparent" outline="none" _hover={{ color: "gray.900" }} userSelect="none"
                            >
                                <Text as="span" fontSize="sm" fontWeight="inherit">{tab.label}</Text>
                                {tab.count > 0 && (
                                    <Badge size="xs" variant="subtle" colorPalette={activeTab === tab.key ? "purple" : "gray"} borderRadius={10} fontSize="2xs" minW="18px" textAlign="center">
                                        {tab.count}
                                    </Badge>
                                )}
                            </Box>
                        ))}
                    </HStack>

                    <Box pt={4}>
                        {activeTab === "data" && <LeadDataTab lead={lead} />}
                        {activeTab === "timeline" && (
                            <LeadTimelineTab timeline={timeline} isLoading={isTimelineLoading} onAddEntry={() => openTimelineModal(leadId)} highlightId={highlightId} onHighlightClear={() => setHighlightId(null)} />
                        )}
                        {activeTab === "notes" && (
                            <LeadNotesTab
                                notes={notes} isLoading={isNotesLoading} onAddNote={handleHeaderAddNote}
                                search={notesSearch} onSearchChange={setNotesSearch}
                                sortDirection={notesSortDirection} onToggleSortDirection={() => setNotesSortDirection(d => d === 'desc' ? 'asc' : 'desc')}
                                highlightId={highlightId} onHighlightClear={() => setHighlightId(null)}
                            />
                        )}
                        {activeTab === "interactions" && <LeadInteractionsTab leadId={leadId} />}
                        {activeTab === "attachments" && (
                            <LeadAttachmentsTab
                                attachments={attachments}
                                isLoading={isAttachmentsLoading}
                                onOpenAttachment={handleOpenAttachment}
                                onDownloadAttachment={handleDownloadAttachment}
                                onUploadAttachments={handleUploadAttachments}
                                onDownloadAllZip={handleDownloadAllZip}
                            />
                        )}
                    </Box>
                </Box>
            </VStack>
            {singleActionModals}
        </>
    );
};

export default LeadDetailPage;
